import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import styles from './albumPage.module.css';
import AlbumPageTrackCard from './AlbumPageTrackCard';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import { API_ENDPOINT } from '../apiEndpoints';

const AlbumPage = ({ artistid, isEmbedded = false, albumid: albumidProp }) => {
  const [trackData, setTrackData] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState('');
  const audioPlayerRef = useRef(null);
  const [currentTitle, setCurrentTitle] = useState('');
  const params = useParams();
  const navigate = useNavigate();
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  
  // Use the prop-passed albumid if available, otherwise use the URL parameter
  const albumid = albumidProp || params.albumid;
  
  const [album, setAlbum] = useState({
    id: '',
    fileurl: '',
    albumname: '',
    artistname: '',
    releasedate: '',
    trackids: '',
    genres: '',
    tags: '',
    credits: '',
  });

  const getTrackNumber = (index) => index + 1;

  useEffect(() => {
    if (!isEmbedded) {
      window.scrollTo(0, 0);
    }
    const albumUrl = `${API_ENDPOINT}/albums/${albumid}`;
    
    axios.get(albumUrl)
      .then(({ data }) => {
        setAlbum(data);
        const trackidRegex = /"(\d+)"/g;
        const tracksString = data.trackids || ''; 
        const parsedTrackids = Array.from(
          tracksString.matchAll(trackidRegex),
          (match) => parseInt(match[1])
        );
    
        const promises = parsedTrackids.map((trackid) => {
          const trackUrl = `${API_ENDPOINT}/tracks/${trackid}`;
          return axios.get(trackUrl);
        });
    
        Promise.all(promises)
          .then((responses) => {
            const trackDetails = responses.map((response, index) => ({
              ...response.data,
              index: index + 1,
            }));
            setTrackData(trackDetails);
            if (trackDetails.length > 0) {
              setSelectedTrack(trackDetails[0]?.location?.fileurl || '');
            }
          })
          .catch((error) => {
            console.log('Error fetching tracks:', error);
          });
      })
      .catch((error) => {
        console.log('Error fetching album:', error);
      });
  }, [albumid, isEmbedded]);

  useEffect(() => {
    if (audioPlayerRef.current && selectedTrack) {
      console.log('Playing track:', selectedTrack);
      audioPlayerRef.current.audio.current.src = selectedTrack;
      audioPlayerRef.current.audio.current.play().catch((error) => {
        console.log('Playback error:', error);
      });
    }
  }, [selectedTrack]);

  const handleSelectTrack = (location, title, index) => {
    setSelectedTrack(location.fileurl || location);
    setCurrentTrackIndex(index);
    setCurrentTitle(title);
  };

  const handleNextTrack = () => {
    const nextIndex = currentTrackIndex + 1;
    if (nextIndex < trackData.length) {
      const loc = trackData[nextIndex]?.location;
      if (loc) {
        setCurrentTrackIndex(nextIndex);
        setCurrentTitle(trackData[nextIndex]?.title);
        setSelectedTrack(loc);
      }
    }
  };

  const handlePrevTrack = () => {
    const prevIndex = currentTrackIndex - 1;
    if (prevIndex >= 0) {
      const loc = trackData[prevIndex]?.location;
      if (loc) {
        setCurrentTrackIndex(prevIndex);
        setCurrentTitle(trackData[prevIndex]?.title);
        setSelectedTrack(loc);
      }
    }
  };

  if (isEmbedded) {
    return (
      <div className={styles.embeddedShell}>
        <div className={styles.tracks}>
          {trackData.map((track, index) => (
            <AlbumPageTrackCard
              key={track.id}
              track={track}
              artistid={track.artistid}
              albumid={track.albumid}
              title={track.title}
              plays={track.plays}
              time={track.time}
              trackNumber={getTrackNumber(index)}
              isSelected={selectedTrack === (track.location?.fileurl || track.location)}
              onSelectTrack={() => handleSelectTrack(track.location, track.title, index)}
            />
          ))}
        </div>
        <div className={styles.Playbar}>
          <AudioPlayer
            ref={audioPlayerRef}
            src={selectedTrack}
            className={styles.rhap_container}
            layout="horizontal-reverse"
            customAdditionalControls={[]}
            customVolumeControls={[]}
            showJumpControls={false}
            showSkipControls={true}
            onClickNext={handleNextTrack}
            onClickPrevious={handlePrevTrack}
            header={`${currentTitle}`}
            onEnded={handleNextTrack}
          />
        </div>
      </div>
    );
  }

  // Rest of your existing return statement for non-embedded view...
  return (
    <div>
      <div className={`${styles.album} ${isEmbedded ? styles.albumEmbedded : ''}`}>
        <div className={styles.column1}>
          <div className={styles.albumCard}>
            <img src={album.fileurl} alt={album.albumname} className={styles.customizeAlbumPhoto} />
            <div className={styles.albumInfo}>
              <div className={styles.albumName}> 
                {album.albumname} 
              </div>
              <div className={styles.info}>
                <div className={styles.releasedate}>
                   {album.releasedate}
                </div>
                <div className={styles.description}>
                  {album.description} 
                </div>
              </div>
              <div className={styles.links}>             
                <div className={styles.linerNotes}>
                </div> 
              </div>
            </div>         
          </div>
        </div>
        <div className={styles.tracks}>
          {trackData.map((track, index) => (
            <AlbumPageTrackCard
              key={track.id}
              track={track}
              artistid={track.artistid}
              albumid={track.albumid}
              title={track.title}
              plays={track.plays}
              time={track.time}
              trackNumber={getTrackNumber(index)}
              isSelected={selectedTrack === (track.location?.fileurl || track.location)}
              onSelectTrack={() => handleSelectTrack(track.location, track.title, index)}
            />
          ))}
        </div>
      </div>
      <div className={styles.Playbar}>
        <AudioPlayer
          ref={audioPlayerRef}
          src={selectedTrack}
          className={styles.rhap_container}
          layout="horizontal-reverse"
          customAdditionalControls={[]}
          customVolumeControls={[]}
          showJumpControls={false}
          showSkipControls={true}
          onClickNext={handleNextTrack}
          onClickPrevious={handlePrevTrack}
          header={`${currentTitle}`}
          onEnded={handleNextTrack}
        />
      </div>
    </div>
  );
};

export default AlbumPage;
